/**
 * src/services/spacesService.ts
 * =============================================================================
 * Cliente de "espacios compartidos" — el modelo multi-organization para
 * estudios. Mapeo directo con `cloudflare/src/routes/spaces.ts`.
 *
 * Vocabulario: en el frontend hablamos de "espacio"; en backend la tabla
 * sigue siendo `organizations`. Es el mismo concepto.
 *
 * El "Mi Espacio personal" es la primera organization del user (la creada
 * automáticamente al signup). Está marcada con `isPersonal: true` en la
 * respuesta de listSpaces — el cliente lo etiqueta sin tener que adivinar
 * orden.
 * =============================================================================
 */

import { api, ApiError } from '@/lib/api'

export type SpaceRole = 'admin' | 'collaborator'

export interface SpaceListItem {
  id: string
  name: string
  role: SpaceRole
  joinedAt: string
  memberCount: number
  /** true para "Mi Espacio personal" (el único que no se puede abandonar). */
  isPersonal: boolean
}

export interface SpaceMember {
  userId: string
  role: SpaceRole
  joinedAt: string
  name: string | null
  email: string
}

export interface SpaceInvitation {
  id: string
  email: string
  createdAt: string
}

export interface SpaceMembersResponse {
  members: SpaceMember[]
  invitations: SpaceInvitation[]
}

export interface SpaceInviteResult {
  email: string
  acceptUrl: string
  emailSent: boolean
  emailError: string | null
}

export interface SpaceInviteInfo {
  invitedEmail: string
  spaceName: string
  inviterName: string
  status: 'pending' | 'accepted' | 'revoked'
  expired: boolean
}

export class SpacesError extends Error {
  code: string
  constructor(code: string, message?: string) {
    super(message ?? code)
    this.code = code
  }
}

function rebrand(e: unknown): SpacesError {
  if (e instanceof ApiError) return new SpacesError(e.code, e.message)
  if (e instanceof Error) return new SpacesError('request_failed', e.message)
  return new SpacesError('request_failed')
}

/** Lista todos los espacios del user (personal + compartidos). */
export async function listSpaces(): Promise<SpaceListItem[]> {
  try {
    const r = await api<{ spaces: SpaceListItem[] }>('/me/spaces', { method: 'GET' })
    return r.spaces ?? []
  } catch (e) {
    throw rebrand(e)
  }
}

/** Crea un espacio nuevo. Pro-only en backend. */
export async function createSpace(name: string): Promise<SpaceListItem> {
  try {
    const r = await api<{ id: string; name: string; role: SpaceRole }>(
      '/me/spaces',
      { method: 'POST', body: { name } },
    )
    // El espacio recién creado tiene 1 miembro (el creador) y NO es personal.
    return {
      id: r.id,
      name: r.name,
      role: r.role,
      joinedAt: new Date().toISOString(),
      memberCount: 1,
      isPersonal: false,
    }
  } catch (e) {
    throw rebrand(e)
  }
}

/** Lista miembros + invitaciones pendientes de un espacio. */
export async function listSpaceMembers(spaceId: string): Promise<SpaceMembersResponse> {
  try {
    return await api<SpaceMembersResponse>(`/space/${spaceId}/members`, { method: 'GET' })
  } catch (e) {
    throw rebrand(e)
  }
}

/** Invita a un email a un espacio. Pro-only en backend. Idempotente. */
export async function inviteToSpace(
  spaceId: string,
  email: string,
): Promise<SpaceInviteResult> {
  try {
    return await api<SpaceInviteResult>(`/space/${spaceId}/invite`, {
      method: 'POST',
      body: { email },
    })
  } catch (e) {
    throw rebrand(e)
  }
}

/** Datos públicos de un invite (para la landing AcceptSpaceInvitePage). */
export async function getSpaceInviteInfo(token: string): Promise<SpaceInviteInfo> {
  try {
    return await api<SpaceInviteInfo>(
      `/space-invite-info?token=${encodeURIComponent(token)}`,
      { method: 'GET' },
    )
  } catch (e) {
    throw rebrand(e)
  }
}

/** Acepta un invite a espacio. Crea membership 'collaborator'. */
export async function acceptSpaceInvite(token: string): Promise<string> {
  try {
    const r = await api<{ spaceId: string }>('/accept-space-invite', {
      method: 'POST',
      body: { token },
    })
    return r.spaceId
  } catch (e) {
    throw rebrand(e)
  }
}

/** Revoca una invitación pendiente. Solo admin del espacio. */
export async function revokeSpaceInvitation(invitationId: string): Promise<void> {
  try {
    await api('/revoke-space-invitation', {
      method: 'POST',
      body: { invitationId },
    })
  } catch (e) {
    throw rebrand(e)
  }
}

/** Expulsa a un miembro. Solo admin. No se puede expulsar a otros admins. */
export async function removeSpaceMember(
  spaceId: string,
  userId: string,
): Promise<void> {
  try {
    await api('/remove-space-member', {
      method: 'POST',
      body: { spaceId, userId },
    })
  } catch (e) {
    throw rebrand(e)
  }
}

/** El propio user abandona un espacio. NO funciona en el espacio personal. */
export async function leaveSpace(spaceId: string): Promise<void> {
  try {
    await api('/leave-space', { method: 'POST', body: { spaceId } })
  } catch (e) {
    throw rebrand(e)
  }
}
